import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAIAssistantChat, loadMessagesFromStorage, saveMessagesToStorage, deleteSessionFromStorage } from '@/hooks/use-ai-assistant-chat';
import { aiAssistantService } from '@/services/ai-assistant.service';
import type { AIAssistantContextType, AISession, AIMessage } from '@/types/ai-assistant.types';

const AIAssistantContext = createContext<AIAssistantContextType | undefined>(undefined);

export { AIAssistantContext };

export function AIAssistantProvider({ children }: Readonly<{ children: React.ReactNode }>): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [sessions, setSessions] = useState<AISession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [backendAvailable, setBackendAvailable] = useState<boolean | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // Track previously saved message count to detect new messages
  const savedMessageCountRef = useRef<number>(0);
  // Track the last session ID we saved messages for to detect session changes
  const lastSavedSessionIdRef = useRef<string | null>(null);

  // Callback to handle responses that arrive after user switched sessions
  const handleBackgroundResponse = useCallback(async (sessionId: string, message: AIMessage) => {
    // Save the message to localStorage for the original session
    const existingMessages = loadMessagesFromStorage(sessionId);
    const updatedMessages = [...existingMessages, message];
    saveMessagesToStorage(updatedMessages, sessionId);
    
    // Also save to backend if available
    if (backendAvailable !== false) {
      try {
        await aiAssistantService.addMessage(sessionId, {
          role: message.role,
          content: message.content,
          timestamp: message.timestamp,
          toolCall: message.toolCall ? {
            id: message.toolCall.id,
            name: message.toolCall.name,
            status: message.toolCall.status,
            result: message.toolCall.result,
          } : undefined,
        });
      } catch (error) {
        console.error('Failed to save background response to backend:', error);
      }
    }
  }, [backendAvailable]);

  // Callback to get current session ID for the hook
  const getCurrentSessionId = useCallback(() => {
    return currentSessionIdRef.current;
  }, []);

  const {
    messages,
    isProcessing,
    currentTool,
    sendMessage: sendChatMessage,
    clearHistory: clearChatHistory,
    setMessages,
    cancelProcessing,
  } = useAIAssistantChat({
    onMessageReceived: () => {
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    },
    onBackgroundResponse: handleBackgroundResponse,
    getCurrentSessionId,
  });

  // Keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-close on navigation
  useEffect(() => {
    const handleNavigation = (): void => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    // Listen for clicks on navigation links
    const handleClick = (e: MouseEvent): void => {
      const target = e.target as HTMLElement;
      // Check if click is on a navigation link
      const navLink = target.closest('a[href], button[data-nav]');
      if (navLink && isOpen) {
        // Small delay to allow navigation to start
        setTimeout(() => setIsOpen(false), 100);
      }
    };

    window.addEventListener('popstate', handleNavigation);
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      document.removeEventListener('click', handleClick);
    };
  }, [isOpen]);

  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const getPageContext = useCallback((): string => {
    const path = window.location.pathname;
    
    if (path.includes('/assets')) return 'assets';
    if (path.includes('/depreciation')) return 'depreciation';
    if (path.includes('/additions')) return 'additions';
    if (path.includes('/revaluation')) return 'revaluation';
    if (path.includes('/audits')) return 'audits';
    if (path.includes('/compliance')) return 'compliance';
    if (path.includes('/tasks')) return 'tasks';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/dashboard')) return 'dashboard';
    
    return 'general';
  }, []);

  const refreshSessions = useCallback(async () => {
    if (backendAvailable === false) return;
    try {
      const fetchedSessions = await aiAssistantService.getSessions();
      setSessions(fetchedSessions.map(s => ({
        id: s.id,
        title: s.title,
        createdAt: s.createdAt,
        updatedAt: s.updatedAt,
        messageCount: s.messageCount,
      })));
      setBackendAvailable(true);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setBackendAvailable(false);
    }
  }, [backendAvailable]);

  const sendMessage = useCallback(async (content: string) => {
    // Auto-create a session if none exists and backend is available
    if (!currentSessionIdRef.current && backendAvailable !== false) {
      try {
        const newSession = await aiAssistantService.createSession();
        // Update both ref and state immediately so messages can be saved
        currentSessionIdRef.current = newSession.id;
        // Also update lastSavedSessionIdRef so new messages get saved (not skipped as "session change")
        lastSavedSessionIdRef.current = newSession.id;
        savedMessageCountRef.current = 0; // Reset to 0 for new session
        setCurrentSessionId(newSession.id);
        await refreshSessions();
      } catch (error) {
        console.error('Failed to auto-create session:', error);
      }
    }
    
    const context = getPageContext();
    await sendChatMessage(content, context);
  }, [sendChatMessage, getPageContext, backendAvailable, refreshSessions]);

  const clearHistory = useCallback(() => {
    clearChatHistory();
  }, [clearChatHistory]);

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const loadSession = useCallback(async (sessionId: string) => {
    // Don't cancel processing - let the request continue in the background
    // The response will be saved to the correct session when it arrives
    
    // Save current session's messages to localStorage before switching
    if (currentSessionIdRef.current && messages.length > 0) {
      saveMessagesToStorage(messages, currentSessionIdRef.current);
    }
    
    // If backend is not available, load from localStorage
    if (backendAvailable === false) {
      const localMessages = loadMessagesFromStorage(sessionId);
      setMessages(localMessages);
      setCurrentSessionId(sessionId);
      return;
    }
    
    try {
      const sessionDetail = await aiAssistantService.getSession(sessionId);
      let loadedMessages: AIMessage[] = sessionDetail.messages.map(m => ({
        id: m.id,
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.content,
        timestamp: m.timestamp,
        toolCall: m.toolCall ? {
          id: m.toolCall.id,
          name: m.toolCall.name,
          status: m.toolCall.status as 'pending' | 'running' | 'success' | 'error',
          result: m.toolCall.result,
        } : undefined,
      }));
      
      // If backend returned no messages, try localStorage as fallback
      if (loadedMessages.length === 0) {
        loadedMessages = loadMessagesFromStorage(sessionId);
      }
      
      setMessages(loadedMessages);
      setCurrentSessionId(sessionId);
    } catch (error) {
      console.error('Failed to load session:', error);
      // Try localStorage as fallback on error
      const localMessages = loadMessagesFromStorage(sessionId);
      setMessages(localMessages);
      setCurrentSessionId(sessionId);
    }
  }, [backendAvailable, setMessages, messages]);

  const startNewSession = useCallback(async () => {
    // Cancel any pending processing before starting a new session
    cancelProcessing();
    
    if (backendAvailable === false) {
      clearChatHistory();
      setCurrentSessionId(null);
      return;
    }
    try {
      const newSession = await aiAssistantService.createSession();
      setCurrentSessionId(newSession.id);
      clearChatHistory();
      await refreshSessions();
    } catch (error) {
      console.error('Failed to create session:', error);
      clearChatHistory();
      setCurrentSessionId(null);
    }
  }, [backendAvailable, clearChatHistory, refreshSessions, cancelProcessing]);

  const deleteSessionHandler = useCallback(async (sessionId: string) => {
    // Always delete from localStorage
    deleteSessionFromStorage(sessionId);
    
    if (currentSessionIdRef.current === sessionId) {
      clearChatHistory();
      setCurrentSessionId(null);
    }
    
    if (backendAvailable === false) return;
    
    try {
      await aiAssistantService.deleteSession(sessionId);
      await refreshSessions();
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  }, [backendAvailable, clearChatHistory, refreshSessions]);

  // Check backend availability and load sessions on mount
  useEffect(() => {
    const checkBackendAndLoadSessions = async () => {
      try {
        const fetchedSessions = await aiAssistantService.getSessions();
        setSessions(fetchedSessions.map(s => ({
          id: s.id,
          title: s.title,
          createdAt: s.createdAt,
          updatedAt: s.updatedAt,
          messageCount: s.messageCount,
        })));
        setBackendAvailable(true);
      } catch (error) {
        console.error('Backend not available for sessions:', error);
        setBackendAvailable(false);
      }
    };
    
    checkBackendAndLoadSessions();
  }, []);

  // Save new messages to backend when they're added
  useEffect(() => {
    const saveNewMessagesToBackend = async () => {
      // Use ref for session ID since it's updated immediately
      const sessionId = currentSessionIdRef.current;
      if (!sessionId || backendAvailable === false) return;
      
      // If session changed, reset the saved count (messages were loaded from backend/storage)
      if (lastSavedSessionIdRef.current !== sessionId) {
        lastSavedSessionIdRef.current = sessionId;
        savedMessageCountRef.current = messages.length;
        return;
      }
      
      if (messages.length <= savedMessageCountRef.current) return;
      
      // Get only the new messages that haven't been saved yet
      const newMessages = messages.slice(savedMessageCountRef.current);
      
      for (const message of newMessages) {
        try {
          await aiAssistantService.addMessage(sessionId, {
            role: message.role,
            content: message.content,
            timestamp: message.timestamp,
            toolCall: message.toolCall ? {
              id: message.toolCall.id,
              name: message.toolCall.name,
              status: message.toolCall.status,
              result: message.toolCall.result,
            } : undefined,
          });
        } catch (error) {
          console.error('Failed to save message to backend:', error);
        }
      }
      
      savedMessageCountRef.current = messages.length;
    };
    
    saveNewMessagesToBackend();
  }, [messages, backendAvailable]);

  const value = useMemo<AIAssistantContextType>(() => ({
    isOpen,
    messages,
    isProcessing,
    unreadCount,
    currentTool,
    openPanel,
    closePanel,
    togglePanel,
    sendMessage,
    clearHistory,
    markAsRead,
    sessions,
    currentSessionId,
    loadSession,
    startNewSession,
    deleteSession: deleteSessionHandler,
    refreshSessions,
  }), [
    isOpen,
    messages,
    isProcessing,
    unreadCount,
    currentTool,
    openPanel,
    closePanel,
    togglePanel,
    sendMessage,
    clearHistory,
    markAsRead,
    sessions,
    currentSessionId,
    loadSession,
    startNewSession,
    deleteSessionHandler,
    refreshSessions,
  ]);

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

export function useAIAssistant(): AIAssistantContextType {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within AIAssistantProvider');
  }
  return context;
}
